<template>
    <div class="corpinfo-edit">
        <v-page-header :back="{
                title: '企业资料',
                goBack: goBack,
            }"
            :title="`${isEdit ? '编辑' : '添加'}关联企业信息`"
        />
        <v-page-header title="关联企业信息" level="section" />
        <div v-if="corpAdmin.need_verify && isEdit" class="verify-status">
            <v-status-bar v-if="info.status == 3" kind="error">
                <reviewFailed :error="info.error" @resetInfo="resetInfo" />
            </v-status-bar>
            <v-status-bar v-if="info.status == 1" kind="wait">
                <span>以下信息正在审核中，预计1到2个工作日可审核完毕，请耐心等待</span>
            </v-status-bar>
        </div>
        <div class="corpinfo-edit-conetnt">
            <v-form ref="form" kind="desc" :class="{'edit-disabled': info.status == 1 && corpAdmin.need_verify}" size="small" @submit="submit">
                <v-form-item label="企业头像" name="avatar" class="form-avatar">
                    <span class="avatar">
                        <img :src="info.avatar || logo" class="avatar-view">
                        <change-avatar-dialog class="avatar-select" @avatarChange="avatarChange" />
                    </span>
                    <div class="tips">
                        支持jpg/jpeg/png，尺寸200X200px，小于2M
                        <br>
                        可用于电话接待页面、员工对外形象的默认头像。应与企业经营相关，且企业需拥有法律认可使用权限，建议使用企业商标、品牌LOGO等。如使用法人或明星肖像，需提供相关证明或授权。
                    </div>
                </v-form-item>
                <div class="form-item-list">
                    <v-form-item label="企业全称" required="请输入企业全称" name="eFullName">
                        <v-input v-model.trim="info.full_name" placeholder="请输入" limit="32" :length-calculator="externalNameCalc" />
                    </v-form-item>
                    <v-form-item label="企业简介" name="eDesc">
                        <v-textarea v-model.trim="info.desc" placeholder="请输入" limit="480" :length-calculator="externalNameCalc" autoheight class="text-area" />
                        <div class="tips">
                            应与企业《营业执照》经营范围相关，建议描述产品及服务相关内容，不得存在违法违禁信息。
                        </div>
                    </v-form-item>
                    <v-form-item label="企业简称" required="请输入企业简称" name="eName">
                        <v-input v-model.trim="info.short_name" placeholder="请输入" limit="12" :length-calculator="externalNameCalc" />
                        <div class="tips">
                            需与企业提交资质合理相关，添加与全称无关的关键字可能导致审核失败
                        </div>
                    </v-form-item>
                    <v-form-item label="企业主页" name="eHome" :rules="rules.eHome">
                        <v-input v-model.trim="info.home" placeholder="请输入" :limit="48" />
                        <div class="tips">
                            请填写完整主页地址，一般以http://或https://等网络协议开头；网站不得存在违法违禁信息。
                            <br>
                            网站ICP备案与企业资质保持一致（如为第三方网站，则需提供合作或授权证明）
                        </div>
                    </v-form-item>
                    <v-form-item label="所属行业" required="请选择所属行业" name="eIndustry" :rules="rules.industry">
                        <selectIndustry :industrylist="industrylist" :e-industry="info.industry" :keyinfo="{area: 'category', areaDetail: 'sub_category'}" @change="changeIndustry" />
                    </v-form-item>
                    <v-form-item label="企业关键字" name="keywords" :rules="rules.keywords">
                        <v-input v-model.trim="info.keywords" placeholder="请输入" />
                        <div class="tips">
                            支持三个关键字，每个不超过8个字符，以空格分割，建议与企业主营范围相关
                        </div>
                    </v-form-item>
                    <v-form-item label="企业地址" required="请选择企业地址" name="eAddress">
                        <selectProvince :e-address="info.address" @change="changeProvince" />
                    </v-form-item>
                    <v-form-item label="企业类型" name="typeofEnterprise">
                        <v-select v-model="info.company_type" placeholder="请输入" :options="typeofEnterpriseList" class="typeofEnterprise" />
                    </v-form-item>
                    <v-form-item label="详细地址" required="请输入企业详细地址" name="eAddressDetail">
                        <v-input v-model.trim="info.address_detail" placeholder="请输入" limit="24" :length-calculator="externalNameCalc" />
                        <div class="tips">
                            直接填写街道、门牌号（如：迎新大道100号），修改此信息直接生效，无需审核
                        </div>
                    </v-form-item>
                    <v-form-item label="联系电话" name="ePhone" desc="修改此信息直接生效，无需审核" :rules="rules.phone">
                        <telSelect ref="telSelect" :phone="info.phone" :sel-type="info.phone_type" @change="changePhone" />
                    </v-form-item>
                    <v-form-item label="邮编" name="zipCode" :rules="rules.zipCode">
                        <v-input v-model.trim="info.postal_code" placeholder="请输入" />
                    </v-form-item>
                    <v-form-item v-if="info.phone_type === 2" label="验证码" name="smscode" :rules="rules.smscode" :required="needSms ? '请输入验证码' : ''">
                        <sms-code ref="smscode" :disabled="!canSendSms" @getSmsCode="getSmsCode" @change="(val) => smscode = val" />
                    </v-form-item>
                    <v-form-item label="传真" name="eFax" desc="修改此信息直接生效，无需审核" :rules="rules.fax">
                        <v-input v-model.trim="info.fax" placeholder="请输入" />
                    </v-form-item>
                    <v-form-item label="电子邮箱" name="eEmail" desc="修改此信息直接生效，无需审核" :rules="rules.email">
                        <v-input v-model.trim="info.email" placeholder="请输入" :limit="48" />
                    </v-form-item>
                    <v-form-item label="企业资质" name="eQualification" class="eQualification">
                        <addQualification :e-qualification="info.attachment" @changeQualification="changeQualification" />
                    </v-form-item>
                </div>
            </v-form>
            <div class="corpinfo-edit-footer">
                <v-footer-actionbar
                    :divider="false"
                    class="corpinfo-edit-footer-actionbar"
                    :layout="{
                        primary: {
                            type: 'submit',
                            text: '确定',
                            disabled: info.status == 1 && corpAdmin.need_verify
                        },
                        cancel: {
                            text: '取消',
                        }
                    }"
                    size="large"
                    @confirm="confirm"
                    @cancel="goBack"
                />
            </div>
        </div>
        <v-dialog v-model="verifyDlg" kind="modal" confirm-text="更新并提交审核">
            <span>需1~2个工作日审核方可生效</span>
        </v-dialog>
    </div>
</template>
<script>
import selectIndustry from '../components/select-industry.vue';
import addQualification from '../components/add-qualification.vue';
import selectProvince from '../components/select-province.vue';
import changeAvatarDialog from '../components/change-avatar-dialog.vue';
import telSelect from '../components/tel-select.vue';
import { logo } from '../utils/defaultConfig';
import reviewFailed from '../components/review-failed.vue';
import smsCode from '../components/sms-code.vue';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { checkIndustry, verifyCode } from '../utils/cgi';
const regPhone = /^1(3\d|4[5-8]|5[0-35-9]|6[567]|7[01345-8]|8\d|9[025-9])\d{8}$/;
const regTel = /^[\d|\-|\+| |#]+$/;
const regFax = /^[/+]?[0-9-]+$/;
export default {
    components: {
        selectIndustry,
        addQualification,
        selectProvince,
        changeAvatarDialog,
        telSelect,
        reviewFailed,
        smsCode,
    },
    props: {
        industrylist: {
            type: Array,
            default: () => [],
        },
        editInfo: {
            type: Object,
            default: () => {},
        },
        corpAdmin: {
            type: Object,
            default: () => ({
                need_verify: true,
                need_attachment: true,
                need_sms_code: true,
                need_related_corp: true,
            }),
        },
    },
    data() {
        return {
            logo,
            preInfo: {},
            isEdit: false,
            info: {
                related_id: '',
                full_name: '',
                short_name: '',
                desc: '',
                signature: '',
                industry: {
                    category: -3,
                    sub_category: -2,
                },
                avatar: '',
                address: {
                    province: '',
                    city: '',
                },
                address_detail: '',
                phone: '',
                email: '',
                home: '',
                status: '',
                last_v_time: '',
                company_type: 1,
                fax: '',
                keywords: '',
                postal_code: '',
                phone_type: 1,
                phone_is_verified: 0,
                attachment: [
                ],
                error: [
                ],
            },
            typeofEnterpriseList: [
                {
                    value: 1,
                    text: '企业单位',
                },
                {
                    value: 2,
                    text: '事业单位或团体',
                },
                {
                    value: 3,
                    text: '个体经营',
                },
            ],
            smscode: '',
            verifyDlg: false,
            rules: {
                email: [
                    {
                        message: '电子邮箱格式有误',
                        validate: (val) => {
                            const reg = /^\w+((-\w+)|(\.\w+))*\@[A-Za-z0-9]+((\.|-)[A-Za-z0-9]+)*\.[A-Za-z0-9]+$/;
                            if (!reg.test(val) && val.length) {
                                return false;
                            }
                            return true;
                        },
                        trigger: 'blur',
                    },
                ],
                eHome: [
                    {
                        message: '请填写完整主页地址，一般以http://或https://等网络协议开头',
                        validate: (val) => {
                            if (!val.match(/^(https?|ftp):\/\//) && val.length) {
                                return false;
                            }
                            return true;
                        },
                        trigger: 'submit',
                    },
                ],
                keywords: [
                    {
                        message: '关键词超过限制',
                        validate: (val) => {
                            if (val.split(' ').length > 3) {
                                return false;
                            }
                            return true;
                        },
                        trigger: 'submit',
                    },
                    {
                        message: '长度超过限制',
                        validate: (val) => {
                            const keywords = val.split(' ');
                            for (let j = 0;j < keywords.length;j++) {
                            if (this.externalNameCalc(keywords[j]) * 2 > 8) {
                                return false;
                            }
                        }
                            return true;
                        },
                        trigger: 'submit',
                    },
                ],
                zipCode: [
                    {
                        message: '邮编格式有误',
                        validate: (val) => {
                        const reg = /^[/+]?[0-9]+$/;
                            if (!reg.test(val) && val.length) {
                                return false;
                            }
                            return true;
                        },
                        trigger: 'submit',
                    },
                ],
                smscode: [
                    {
                        message: '验证码必须为6位',
                        validate: (val) => {
                            if (val.length && val.length !== 6 && this.needSms) {
                                return false;
                            }
                            return true;
                        },
                        trigger: 'submit',
                    },
                ],
                phone: [
                    {
                        message: '手机号格式错误',
                        validate: () => {
                            if (this.info.phone && !regPhone.test(this.info.phone) && this.info.phone_type === 2) {
                                return false;
                            }
                            return true;
                        },
                        trigger: 'submit',
                    },
                    {
                        message: '座机号码格式错误',
                        validate: () => {
                            if (this.info.phone && !regTel.test(this.info.phone) && this.info.phone_type === 1) {
                                return false;
                            }
                            return true;
                        },
                        trigger: 'submit',
                    },
                ],
                industry: [{
                    message: '请选择行业',
                    validate: (val) => {
                        if (val && val.length && val[0] > 0 && val[1] > 0) return true;
                        return false;
                    },
                    trigger: 'submit',
                }],
                fax: [{
                    message: '传真格式有误',
                    validate: (val) => {
                        if (val && !regFax.test(val)) return false;
                        return true;
                    },
                    trigger: 'submit',
                }],
            },

        };
    },
    computed: {
        needSms() {
            // 更改手机号需要验证码，新增时候可选（验证码非必填）
            const { need_sms_code } = this.corpAdmin;
            const res = (this.info.phone_type === 2 && this.info.phone !== this.preInfo.phone && this.info.phone.length > 0 && need_sms_code);
            return res;
        },
        canSendSms() {
            return regPhone.test(this.info.phone);
        },
    },
    created() {
        if (this.editInfo?.related_id) {
            this.isEdit = true;
            this.preInfo = cloneDeep(this.editInfo);
            this.info = this.editInfo;
        } else {
            this.preInfo = cloneDeep(this.info);
        }
    },
    methods: {
        getSmsCode() {
            this.$refs.smscode?.getSmsCode({
                phone: this.info.phone,
            });
        },
        resetInfo() {
            this.$ajax.gatewayPost('/v1/basic/corp/corp/renewRelatedCorpInfo', {
                related_id: this.info.related_id,
            }).then(() => {
                this.$emit('goBack');
            })
            .catch((err) => {
                this.$message.error(err.message || '网络故障，请稍后再试');
            });
        },
        externalNameCalc(str) {
            if (str == null) return 0;
            if (typeof str != 'string') {
                str += '';
            }
            return Math.round(str.replace(/[^\x00-\xff]/g, '01').length / 2);
        },
        changeProvince(val) {
            this.info.address = val;
        },
        changeIndustry(val) {
            this.info.industry = {
                category: val.area,
                sub_category: val.areaDetail,
            };
        },
        avatarChange(url) {
            this.$set(this.info, 'avatar', url);
        },
        changeQualification(data) {
            this.info.attachment = data;
        },
        customFn(res) {
            if (res.code !== 0) {
                this.$message.error(res.message || res.msg || '网络故障，请稍后再试');
            }
        },
        goBack() {
            const that = this;
            this.$confirm({
                title: '返回企业资料？',
                content: '当前编辑的信息将不会保存。',
                confirmText: '返回',
                confirm() {
                    that.$emit('goBack');
                },
            });
        },
        changePhone(val) {
            this.info.phone = val.phone;
            this.$set(this.info, 'phone_type', val.phoneType);
        },
        confirm(e) {
            e.preventDefault();
            this.$refs.form.submit();
        },
        submit(e) {
            e.preventDefault();
            this.formSubmit();
        },
        async formSubmit() {
            // 校验行业
            if (!await checkIndustry(this.info.industry)) return;
            // 校验短信验证码
            if (this.needSms) {
                const res =  await verifyCode({
                    phone: this.info.phone,
                    code: this.smscode,
                    related_id: this.info.related_id,
                });
                if (!res) return;
            }
            const params = {
                ...this.info,
                attachment: this.info.attachment.filter(item => item.fileName),
            };
            if ((this.isEdit && this.need_verify(params)) || !this.isEdit) {
                const that = this;
                this.$confirm({
                    content: '需1~2个工作日审核方可生效',
                    confirmText: `${this.isEdit ? '更新并' : ''}提交审核`,
                    confirm() {
                        that.update(params);
                    },
                });
            } else {
                this.update(params);
            }
        },
        update(params) {
            const data = {
                relatedId: params.related_id,
                operating: params.related_id ? 'update' : 'add',
                eFullName: params.full_name,
                eName: params.short_name,
                eDesc: params.desc,
                signature: params.signature,
                eIndustry: {
                    area: params.industry.category,
                    areaDetail: params.industry.sub_category,
                },
                eLogo: params.avatar,
                eAddress: params.address,
                eAddressDetail: params.address_detail,
                ePhone: params.phone,
                eEmail: params.email,
                eHome: params.home,
                status: params.status,
                lastVTime: params.last_v_time,
                typeofEnterprise: params.company_type,
                eFax: params.fax,
                keywords: params.keywords,
                zipCode: params.postal_code,
                phoneType: params.phone_type,
                phoneIsVerified: params.phone_is_verified,
                eQualification: params.attachment,
                error: params.error,
            };
            this.$ajax.post('/mng/account/RelatedCorp/Create', data).then((res) => {
                this.$message.success(this.info.related_id ? '修改成功' : '添加成功');
                this.$emit('goBack');
            });
        },
        need_verify(params) {
            const keys = ['full_name', 'postal_code', 'short_name', 'desc', 'avatar', 'industry', 'home', 'keywords', 'company_type', 'attachment'];
            const preVerifyInfo = {};
            const info = {};
            keys.forEach((key) => {
                preVerifyInfo[key] = this.preInfo[key];
                info[key] = params[key];
            });
            if (isEqual(preVerifyInfo, info)) return false;
            return true;
        },
    },
};
</script>
<style lang="less" scoped>
.verify-status {
    margin-bottom: 20px;
}
.edit-disabled {
    opacity: 0.4;
    cursor: default;
    -ms-pointer-events: none;
    pointer-events: none;
}
.tips {
    color: var(--color-text-caption);
    font-size: 12px;
}
.form-item-list {
    display: grid;
    grid-template-columns: repeat(2, calc(50% - 32px));
    grid-gap: 0 64px;
}
/deep/ .v-textarea, .v-input {
    width: 100%;
}
/deep/ .text-area {
    max-height: 110px !important;
    .v-textarea__txt {
        display: inline-flex;
        max-height: 80px;
        height: inherit;
        overflow-y: auto;
    }
}
.upload {
    width: 100%;
}
.avatar {
    display: inline-flex;
    height: 48px;
    align-items: center;
    &-view {
        width: 48px;
        height: 48px;
        border-radius: 100%;
        display: inline-block;
        margin-right: 9px;
    }
}
.form-avatar {
    width: calc(50% + 32px);
}
/deep/ .v-description-item--text-content {
    flex: 0 0 auto;
}
.corpinfo-edit-footer {
    /deep/ .v-description-item--text-content {
        width: 0px;
    }
    &-actionbar {
        width: -webkit-fill-available;
        position: fixed;
        bottom: 0px;
        z-index: 1;
        background-color: #fff;
    }
}
.typeofEnterprise {
    width: 100%;
}
.eQualification {
    width: calc(100% + 62px);
    margin-bottom: 73px;
}
</style>

